import { CONSTANT, RATING_FORM_UI_SCHEMA_VERSION, RATING_FORM_UI_VERSION_TS1_V3, RATING_FORM_UI_VERSION_TS1_V4 } from "src/app/constant";
import { DraftItem, TS1FormResponseJSON } from "src/app/models/models";
import { inferFormConfigVersion, withFormConfigVersion } from "./form-config-version.utils";
import { mapFormTypeCode } from "./form-type-code.utils";
import { resolveInitialRatingFormVersion, resolveRatingFormVersionForLoad } from "./rating-form-version.utils";
import { RatingSessionShellCache } from "./rating-session-persistence.utils";

export { mapFormTypeCode } from "./form-type-code.utils";

export function isSameRatingRoute(
	routeKey: string,
	activeRouteKey: string | null,
	draft?: DraftItem | null,
	formSnapshot?: TS1FormResponseJSON | null,
	sessionShell?: RatingSessionShellCache | null,
): boolean {
	if (!routeKey) return false;
	if (activeRouteKey != null && activeRouteKey === routeKey) return true;
	if (sessionShell?.routeKey === routeKey) return true;
	if (draft?.id === routeKey) return true;
	const formId = draft?.trainingInformation?.formId ?? draft?.formDetails?.id ?? formSnapshot?.id;
	return formId != null && formId === routeKey;
}

/** Clear persisted session shell only when navigating to a different rating route. */
export function shouldClearRatingSessionState(activeRouteKey: string | null, routeKey: string): boolean {
	if (!routeKey) return false;
	return activeRouteKey != null && activeRouteKey !== routeKey;
}

/** Merge in-memory form snapshot into draft for bootstrap when draft formDetails omit configVersion. */
export function effectiveDraftForBootstrap(
	draft: DraftItem,
	formSnapshot?: TS1FormResponseJSON | null,
): DraftItem {
	if (!formSnapshot) return draft;

	const draftFd = draft.formDetails;
	if (!draftFd) {
		return { ...draft, formDetails: formSnapshot };
	}

	const snapCv = formSnapshot.configVersion;
	if (snapCv != null && !Number.isNaN(Number(snapCv)) && draftFd.configVersion == null) {
		return { ...draft, formDetails: withFormConfigVersion(draftFd, Number(snapCv)) };
	}

	if (draftFd.configVersion == null) {
		const mergedForInfer = {
			...draftFd,
			assessmentConfig: draftFd.assessmentConfig?.length
				? draftFd.assessmentConfig
				: formSnapshot.assessmentConfig,
		};
		return {
			...draft,
			formDetails: withFormConfigVersion(draftFd, inferFormConfigVersion(mergedForInfer)),
		};
	}

	return draft;
}

/**
 * Synchronously restore `ratingFormUiVersion` after hot reload while async IndexedDB load runs.
 */
export function bootstrapRatingFormUiVersion(params: {
	routeKey: string;
	activeRouteKey: string | null;
	draft?: DraftItem | null;
	formTypeCode?: string | null;
	formSnapshot?: TS1FormResponseJSON | null;
	sessionShell?: RatingSessionShellCache | null;
}): number | null {
	const { routeKey, activeRouteKey, draft, formTypeCode, formSnapshot, sessionShell } = params;

	if (!isSameRatingRoute(routeKey, activeRouteKey, draft, formSnapshot, sessionShell)) {
		return null;
	}

	if (draft) {
		const effectiveDraft = effectiveDraftForBootstrap(draft, formSnapshot);
		return resolveRatingFormVersionForLoad(effectiveDraft);
	}

	const code =
		formTypeCode ??
		formSnapshot?.formType?.code ??
		sessionShell?.formType;
	const formType = mapFormTypeCode(code);

	if (formSnapshot && formType) {
		return resolveInitialRatingFormVersion(formType, formSnapshot);
	}

	const shellVersion = sessionShell?.ratingFormUiVersion;
	if (shellVersion != null && !Number.isNaN(Number(shellVersion))) {
		return Number(shellVersion);
	}

	return null;
}

/** Synchronously restore container `formType` after hot reload. */
export function bootstrapFormType(params: {
	routeKey: string;
	activeRouteKey: string | null;
	draft?: DraftItem | null;
	serviceFormTypeCode?: string | null;
	formSnapshot?: TS1FormResponseJSON | null;
	sessionShell?: RatingSessionShellCache | null;
}): string {
	const { routeKey, activeRouteKey, draft, serviceFormTypeCode, formSnapshot, sessionShell } = params;

	if (!isSameRatingRoute(routeKey, activeRouteKey, draft, formSnapshot, sessionShell)) {
		return "";
	}

	if (serviceFormTypeCode) {
		return mapFormTypeCode(serviceFormTypeCode);
	}
	if (draft?.formDetails?.formType?.code) {
		return mapFormTypeCode(draft.formDetails.formType.code);
	}
	if (formSnapshot?.formType?.code) {
		return mapFormTypeCode(formSnapshot.formType.code);
	}
	if (sessionShell?.formType) {
		return mapFormTypeCode(sessionShell.formType);
	}
	return "";
}

/** Which TS-1 shell the container template should mount (null = wait, do not default to v2). */
export function resolveTs1ShellForTemplate(
	ratingFormUiVersion: number | null,
): "v4" | "v3" | "v2" | "pending" {
	if (ratingFormUiVersion === RATING_FORM_UI_VERSION_TS1_V4) return "v4";
	if (ratingFormUiVersion === RATING_FORM_UI_VERSION_TS1_V3) return "v3";
	if (ratingFormUiVersion === RATING_FORM_UI_SCHEMA_VERSION) return "v2";
	return "pending";
}
